import { type ApolloError, useQuery } from '@apollo/client';
import { Order_By } from '@openx/types/gqlTypes';
import { useMemo } from 'react';
import { GetSegmentsDocument } from '../../../../../../graphql/query/audiences/GetSegments.generated';
import type { FormSegment } from '../../../../../../modules/audiences/AudienceForm/types';

import { LocationSegmentType } from '../constants';

export type UseLocationOptionsResponse = {
  loading: boolean;
  error?: ApolloError;
  data: {
    stateSegments: FormSegment[];
    dmaSegments: FormSegment[];
    congressionalSegments: FormSegment[];
  };
};

export const useLocationOptions = (): UseLocationOptionsResponse => {
  const {
    loading,
    error,
    data: { segment: allSegments = [] } = {},
  } = useQuery(GetSegmentsDocument, {
    variables: {
      order_by: [{ full_name: Order_By.Asc }],
      where: {
        _and: [
          { category: { _eq: 'location' } },
          { is_archived: { _eq: false } },
          {
            _or: [
              {
                _and: [{ sub_category: { _eq: LocationSegmentType.Congressional } }],
              },
              {
                _and: [
                  { sub_category: { _in: [LocationSegmentType.State, LocationSegmentType.DMA] } },
                  { is_nav_only: { _neq: true } },
                ],
              },
            ],
          },
        ],
      },
    },
  });

  const { stateSegments, dmaSegments, congressionalSegments } = useMemo(() => {
    const stateSegmentsRaw = allSegments.filter(segment => segment.sub_category === LocationSegmentType.State);
    const dmaSegmentsRaw = allSegments.filter(segment => segment.sub_category === LocationSegmentType.DMA);
    const congressionalSegmentsRaw = allSegments.filter(
      segment => segment.sub_category === LocationSegmentType.Congressional
    );

    return {
      stateSegments: stateSegmentsRaw.filter(segment => segment.is_nav_only || segment.is_leaf),
      dmaSegments: dmaSegmentsRaw.filter(segment => segment.is_nav_only || segment.is_leaf),
      congressionalSegments: congressionalSegmentsRaw,
    };
  }, [allSegments]);

  return {
    loading,
    error,
    data: {
      stateSegments,
      dmaSegments,
      congressionalSegments,
    },
  };
};
